import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import MainLayout from "../../layouts/main_layout/main_layout";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../providers/theme/colors/colors";
import useDexcoMembers from "./use_dexco_members";
import { DEXCO_CATEGORY_OPTIONS, DexcoCategory } from "./dexco_data";

function DexcoMemberCard({
  name,
  post,
  place,
  img,
}: {
  name: string;
  post: string;
  place?: string;
  img?: string;
}) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: { xs: 3, md: 4 },
        border: `1px solid rgba(56, 136, 194, 0.16)`,
        boxShadow: "0 14px 30px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(244, 250, 255, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
      }}
    >
      <Box
        sx={{
          height: 8,
          background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
        }}
      />
      <Box
        px={{ xs: 1.75, md: 2 }}
        py={{ xs: 2.5, md: 3 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        height="100%"
      >
        {img ? (
          <Box
            component="img"
            src={img}
            alt={name}
            sx={{
              width: { xs: 150, sm: 170, md: 180 },
              height: { xs: 180, sm: 200, md: 215 },
              mb: 2,
              borderRadius: 3,
              objectFit: "cover",
              objectPosition: "center top",
              border: `4px solid ${SECONDARY_THEME_COLOR}`,
              boxShadow: "0 12px 24px rgba(56, 136, 194, 0.16)",
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: { xs: 150, sm: 170, md: 180 },
              height: { xs: 180, sm: 200, md: 215 },
              mb: 2,
              borderRadius: 3,
              bgcolor: `${SECONDARY_THEME_COLOR}33`,
              color: MAIN_THEME_COLOR,
              border: `4px solid ${SECONDARY_THEME_COLOR}`,
              boxShadow: "0 12px 24px rgba(56, 136, 194, 0.16)",
              fontSize: { xs: "2.6rem", md: "3.1rem" },
              fontWeight: 700,
            }}
          >
            {name.charAt(0)}
          </Avatar>
        )}
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ mb: 0.5, fontSize: { xs: 18, md: 20 } }}
        >
          {name}
        </Typography>
        {place ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.75, lineHeight: 1.5, textAlign: "center" }}
          >
            {place}
          </Typography>
        ) : null}
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            lineHeight: 1.6,
            fontWeight: 700,
            fontSize: "0.9rem",
            textAlign: "center",
          }}
        >
          {post}
        </Typography>
      </Box>
    </Card>
  );
}

export default function DexcoPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { members, loading } = useDexcoMembers();
  const [selectedTab, setSelectedTab] = useState(0);

  const selectedCategory = DEXCO_CATEGORY_OPTIONS[selectedTab] as DexcoCategory;
  const currentMembers = useMemo(
    () => members.filter((member) => member.category === selectedCategory),
    [members, selectedCategory],
  );

  return (
    <MainLayout>
      <Box mt={{ xs: 2, md: 3 }} mb={{ xs: 4, md: 5 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
          DEXCO
        </Typography>
        <Typography
          variant="h6"
          textAlign="center"
          color="text.secondary"
          mt={1}
          sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, px: 1 }}
        >
          ICYM, YCS, and YSM diocesan executive teams
        </Typography>

        <Box
          mt={3}
          p={{ xs: 2.5, md: 3.5 }}
          sx={{
            borderRadius: 4,
            background:
              "radial-gradient(circle at top left, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 28%), linear-gradient(135deg, #0b466f 0%, #1f6ea9 45%, #54a9dd 100%)",
            color: "#fff",
            boxShadow: "0 22px 48px rgba(10, 53, 89, 0.2)",
          }}
        >
          <Typography textAlign="center" sx={{ maxWidth: 860, mx: "auto", lineHeight: 1.8, opacity: 0.94 }}>
            Explore the current diocesan executive teams across ICYM, YCS, and YSM.
          </Typography>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              fullWidth={isMobile}
              sx={{
                maxWidth: { xs: "100%", sm: 240 },
                bgcolor: "#ffffff",
                color: MAIN_THEME_COLOR,
                ":hover": {
                  bgcolor: "#f4f8fb",
                },
              }}
              onClick={() => navigate("/about")}
            >
              Back to About Us
            </Button>
          </Box>
        </Box>

        <Box mt={4} sx={{ overflowX: "auto" }}>
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: { xs: 0.5, md: 1 },
              },
            }}
          >
            <Tab label="ICYM DEXCO" />
            <Tab label="YCS DEXCO" />
            <Tab label="YSM DEXCO" />
          </Tabs>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} mt={1}>
            {currentMembers.map((member) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={member.docId || `${member.category}-${member.rank}-${member.name}`}>
                <DexcoMemberCard {...member} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </MainLayout>
  );
}
